import React, { useContext, useEffect } from "react";
import UserContext from "./context/UserContext";

import "./css/index.css";
import "./css/themes/v2/_index.css";
import "./css/amyvid.css";

import amyvidLogo from "./assets/AMYVID_RM_H_Prom_Adm_RGB_FC_Pos.svg";
import FooterLogo from "./assets/lillylogotag.svg";
import LdsHeader from "./components/LdsHeader";
import LdsFooter from "./components/LdsFooter";
import LdsLink from "./components/LdsLink";
import LdsIsi from "./components/LdsIsi";
import LdsButton from "./components/LdsButton";
import LdsAlert from "./components/LdsAlert";
import LdsUtilityMenuLink from "./components/LdsHeader/UtilityMenu/UtilityMenuLink";
import LdsContactLilly from "./components/LdsContactLilly";
import { ReactLoaderSpinner } from "./components/ReactLoaderSpinner";
import { PasswordProtectedModal } from "./components/PasswordProtectedModal/index.js";

import { LdsModalProvider } from './components/LdsModal/LdsModalProvider.jsx';
import HcpModal from "./components/LdsModal/hcpModal.jsx";

// routes
import router from "./router.js";
import { RouterProvider } from "react-router-dom";

// services
import { forceNavigate } from "./store/utils/helper";
import {
  useGetCompanyNumberQuery,
  useIsAuthUserQuery,
} from "./store/services/authServerApi";

function App() {
  const token = useContext(UserContext);
  const { pathname } = window.location;

  const isAutherizedPasswordProtected = sessionStorage.getItem(
    "isAutherizedPasswordProtected"
  );

  const { isError } = useIsAuthUserQuery(null, {
    skip: !sessionStorage.getItem("token"),
  });

  const { isLoading: isCompanyNumberFetched, data: { data = {} } = {} } =
    useGetCompanyNumberQuery(null, {
      skip: ~~sessionStorage.getItem("companyNo"), // skip if sessionStorage has companyNumber
    });

  if (~~data?.companyNumber) {
    sessionStorage.setItem("companyNo", ~~data?.companyNumber);
    sessionStorage.setItem("company", JSON.stringify(data));
  }

  // useEffect
  useEffect(() => {
    const head = document.head;
    const link = document.createElement("link");
    link.type = "text/css";
    link.rel = "stylesheet";
    link.href = process.env.REACT_APP_COOKIE_SETTING_BANNER_MODAL_CSS;
    head.appendChild(link);

    const loaderScript = document.createElement("script");
    loaderScript.src = process.env.REACT_APP_COOKIE_SETTING_BANNER_MODAL_JS;
    loaderScript.async = true;
    document.body.appendChild(loaderScript);

    // on unmout
    return () => {
      document.body.removeChild(loaderScript);
      head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  // custom function
  const Logout = () => {
    sessionStorage.removeItem("token");
    forceNavigate("/logout");
  };
  console.log("process.env :>> ", process.env);
  if (isError) {
    Logout();
  }

  const openConsentSettings = (e) => {
    e.preventDefault();
    window.CassieWidgetLoader.Widget.showModal();
  }

  return (
    <div className="App">
      {isCompanyNumberFetched && <ReactLoaderSpinner />}

      {process?.env?.REACT_APP_ENV === "test" &&
        !isAutherizedPasswordProtected && <PasswordProtectedModal />}
      <LdsAlert id="hcpAlert" level="info" className="compact ps-5 pe-5" inline>
        <div className="container"></div>This site is intended for US healthcare
        professionals only
      </LdsAlert>

      <LdsHeader
        currentRoute={pathname}
        customLogo={
          <a href="https://www.amyvid.com" target="_blank" title="Link to homepage" rel="noreferrer">
            <img
              src={amyvidLogo}
              alt="AMYViD® (Florbetapir F 18 Injection)"
              style={{ width: "200px", padding: "1rem 0" }}
            />
          </a>
        }
        utilityMenuCustomLinkItemsPosition="after"
        utilityMenuLinks={[
          <LdsUtilityMenuLink
            href="https://pi.lilly.com/us/amyvid-uspi.pdf?s=pi"
            key="pre"
            target="_blank"
          >
            Prescribing Information
          </LdsUtilityMenuLink>,
        ]}
        contactLilly={<><LdsContactLilly options={
          {
            phone: {
              standard: 'Call (800) 545-5979',
              pretty: '1-800-LillyRx'
            }
          }
        } /></>}
      >
        <LdsHeader.Link href="/">Home</LdsHeader.Link>
        <LdsHeader.Link href="/about">About Amyvid</LdsHeader.Link>
        <LdsHeader.Link href="/overview">Reader Training</LdsHeader.Link>
        <LdsHeader.Link href="/resources">Resources</LdsHeader.Link>
        <LdsHeader.Link href="/profile">My Profile</LdsHeader.Link>
        {!token && (
          <LdsHeader.Link href="/login">Login/Register</LdsHeader.Link>
        )}

        {token && (
          <LdsHeader.Link href="#" onClick={() => Logout()}>
            Logout
          </LdsHeader.Link>
        )}
      </LdsHeader>

      <div className="pageContent">
        <RouterProvider router={router} />
      </div>

      <LdsFooter
        customLogo={
          <a href="https://www.lilly.com">
            <img
              src={FooterLogo}
              alt="Lilly - A Medicine Company"
              style={{ width: "200px", padding: "1rem 0" }}
            />
          </a>
        }
        customerSupportContent={
          <p>
            <strong>To speak to customer support:</strong>
            <br />
            Mon - Fri, 8 AM - 8 PM EST
            <br />
            <LdsLink href="tel:+1(800)545-5979">1-800-LillyRx</LdsLink><br />
            <LdsLink href="tel:+1(800)545-5979">Call (800) 545-5979</LdsLink>
          </p>
        }
        globalLinks={
          <>
            <LdsFooter.Link
              href="https://www.lillyhub.com/legal/lillyusa/english/termsofuse.html"
              target="_blank"
            >
              Terms of Use
            </LdsFooter.Link>
            <LdsFooter.Link
              href="https://privacynotice.lilly.com/"
              target="_blank"
            >
              Privacy Statement
            </LdsFooter.Link>
            <LdsFooter.Link
              href="https://www.lillyhub.com/ux/lillyusa/english/accessibility.html"
              target="_blank"
            >
              Accessibility Statement
            </LdsFooter.Link>
            <LdsFooter.Link href="/sitemap" target="_blank">
              Sitemap
            </LdsFooter.Link>
          </>
        }
        mainLinks={
          <>
            <LdsFooter.Link
              href="https://pi.lilly.com/us/amyvid-uspi.pdf?s=pi"
              target="_blank"
            >
              Prescribing Information
            </LdsFooter.Link>
          </>
        }
        privacyControls={
          <>
            <LdsButton
              classes="compact utility lds-button-reset-cookie-consent"
              onClick={(e) => {
                openConsentSettings(e)
              }}
            >
              Cookie Consent Settings
            </LdsButton>
            <LdsButton
              classes="compact utility lds-button-privacy-choices"
              onClick={(e) => {
                e.preventDefault();
                const newWindow = window.open(
                  "https://privacynotice.lilly.com/#california-residents",
                  "_blank",
                  "noopener,noreferrer"
                );
                if (newWindow) newWindow.opener = null;
                console.log("Privacy Choices Clicked");
              }}
            >
              <title>toggle</title>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="33"
                height="16"
                viewBox="0 0 33 16"
                fill="none"
                aria-hidden="true"
                role="img"
                focusable="false"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M24.8288 0H8.17123C3.89726 0 0.5 3.54286 0.5 8C0.5 12.4571 3.89726 16 8.17123 16H24.8288C29.1027 16 32.5 12.4571 32.5 8C32.5 3.54286 28.9932 0 24.8288 0ZM1.81507 8C1.81507 4.34286 4.66438 1.37143 8.17123 1.37143H19.0205L15.6233 14.6286H8.17123C4.66438 14.6286 1.81507 11.6571 1.81507 8Z"
                  fill="#212121"
                />
                <path
                  d="M27.0205 4.57145C27.2397 4.80002 27.2397 5.25717 27.0205 5.48574L24.7191 8.00002L27.1301 10.5143C27.3493 10.7429 27.3493 11.2 27.1301 11.4286C26.9109 11.6572 26.4725 11.6572 26.2534 11.4286L23.8424 8.91431L21.4314 11.4286C21.2123 11.6572 20.7739 11.6572 20.5547 11.4286C20.3356 11.2 20.3356 10.7429 20.5547 10.5143L22.8561 8.00002L20.4451 5.48574C20.226 5.25717 20.226 4.80002 20.4451 4.57145C20.6643 4.34288 21.1027 4.34288 21.3219 4.57145L23.7328 7.08574L26.1438 4.57145C26.363 4.34288 26.8013 4.34288 27.0205 4.57145Z"
                  fill="white"
                />
                <path
                  d="M13.9793 4.68571C14.1985 4.91428 14.3081 5.37143 14.0889 5.6L9.48617 11.2C9.37659 11.3143 9.267 11.4286 9.15741 11.4286C8.93823 11.5429 8.60946 11.5429 8.39028 11.3143L5.97932 8.8C5.76015 8.57143 5.76015 8.11428 5.97932 7.88571C6.1985 7.65714 6.63686 7.65714 6.85604 7.88571L8.82864 9.82857L12.993 4.68571C13.2122 4.45714 13.6506 4.45714 13.9793 4.68571Z"
                  fill="#212121"
                />
              </svg>
              Your Privacy Choices
            </LdsButton>
          </>
        }
      >
        <p>
          This site is intended for healthcare professionals only.
          <br />
          Amyvid<sup>&reg;</sup> is a registered trademark owned or licensed by
          Eli Lilly and Company, its subsidiaries, or affiliates.
        </p>
        <p>
          Lilly Support Services<sup>&trade;</sup> is a trademark owned or
          licensed by Eli Lilly and Company, its subsidiaries, or affiliates.
        </p>
        <p>
          Other product/company names mentioned herein are the trademarks of their respective owners.
        </p>
        <p className="mt-0 mb-3">
          PP-AM-US-0629 06/2025 &nbsp;
          <LdsLink
            href="https://www.lillyhub.com/legal/lillyusa/english/copyright.html"
            target="_blank"
          >
            © Lilly USA, LLC 2025. All rights&nbsp;reserved.
          </LdsLink>
        </p>
      </LdsFooter>
      <LdsIsi
        isiHeading="Important Safety Information"
        defaultContent={
          <>
            {/* The main ISI Column/Panel content goes here */}
            <div className="content-block">
              <p className="fz-md b mb-1">
                Risk for Image Misinterpretation and Other Errors
              </p>
              <ul className="mb-2">
                <li>
                  Errors may occur in the Amyvid estimation of brain neuritic
                  plaque density during image interpretation{" "}
                </li>
                <li>
                  Image interpretation should be performed independently of the
                  patient’s clinical information. The use of clinical
                  information in the interpretation of Amyvid images has not
                  been evaluated and may lead to errors. Other errors may be due
                  to extensive brain atrophy that limits the ability to
                  distinguish gray and white matter on the Amyvid scan, as well
                  as motion artifacts that distort the image
                </li>
                <li>
                  Amyvid scan results are indicative of the brain neuritic
                  amyloid plaque content only at the time of image acquisition
                  and a negative scan result does not preclude the development
                  of brain amyloid in the future
                </li>
              </ul>
              <p className="fz-md b mt-2 mb-1">Radiation Risk</p>
              <ul>
                <li>
                  Amyvid, similar to other radiopharmaceuticals, contributes to
                  a patient’s overall long-term cumulative radiation exposure.
                  Long-term cumulative radiation exposure is associated with an
                  increased risk of cancer. Ensure safe handling to protect
                  patients and health care workers from unintentional radiation
                  exposure
                </li>
              </ul>
              <p className="mt-3 mb-2">
                The <strong>most common adverse reactions</strong> reported in
                clinical trials were headache (1.8%), musculoskeletal pain
                (0.7%), blood pressure increased (0.7%), nausea (0.7%), fatigue
                (0.5%), and injection site reaction (0.5%)
              </p>
              <p className="mt-3 mb-3">AM HCP ISI 14SEP2022</p>
              <div className="mt-2">
                <LdsLink
                  href="https://pi.lilly.com/us/amyvid-uspi.pdf?s=pi"
                  ariaLabel="Please see Full Prescribing Information for Amyvid."
                  target="_blank"
                  classes="black"
                >
                  <strong>Please see Full Prescribing Information for Amyvid.</strong>
                </LdsLink>
              </div>
            </div>
          </>
        }
        indicationHeading="Indication"
        indicationsContent={
          <>
            {/* The main Indication Column/Panel content goes here */}
            <div className="content-block">
              <p className="mb-2">
                Amyvid is a radioactive diagnostic agent indicated for Positron Emission Tomography (PET) imaging of the brain to estimate beta-amyloid neuritic plaque density in adult patients with cognitive impairment who are being evaluated for Alzheimer's Disease (AD) and other causes of cognitive decline.
              </p>
              <p className="mb-2">
                A negative Amyvid scan indicates sparse to no neuritic plaques
                and is inconsistent with a neuropathological diagnosis of AD at
                the time of image acquisition; a negative scan result reduces
                the likelihood that a patient's cognitive impairment is due to
                AD. A positive Amyvid scan indicates moderate to frequent
                amyloid neuritic plaques; neuropathological examination has
                shown this amount of amyloid neuritic plaque is present in
                patients with AD, but may also be present in patients with other
                types of neurologic conditions, as well as older people with
                normal cognition. Amyvid is an adjunct to other diagnostic
                evaluations.
              </p>
              <p>
                Amyvid for intravenous use is supplied in multidose vials
                containing 500-1900 MBq/mL Florbetapir F 18.
              </p>

              <p>
                <strong>Limitations of Use:</strong>
                <ul>
                  <li>
                    Amyvid scan does not establish a diagnosis of AD or other
                    cognitive disorder
                  </li>
                  <li>
                    Safety and effectiveness of Amyvid have not been established
                    for:
                  </li>
                  <ul>
                    <li>
                      Predicting development of dementia or other neurologic
                      condition
                    </li>
                    <li>Monitoring responses to therapies</li>
                  </ul>
                </ul>
              </p>
            </div>
          </>
        }
        hiddenIsiPanelExpandText="Safety Summary with Warnings and Indications information. Select to Expand."
        hiddenIsiPanelCollapseText="Safety Summary with Warnings and Indications information. Select to Collapse."
        hiddenIsiContentCollapseText="Safety Summary with Warnings. Select to Collapse."
        hiddenIsiContentExpandText="Safety Summary with Warnings. Select to Expand."
        hiddenIsiIndicationsCollapseText="Indications information. Select to Collapse."
        hiddenIsiIndicationsExpandText="Indications information. Select to Expand"
        warningBlockHeading=""
        warningBlockParagraph=""
      />

      <LdsModalProvider>
        <HcpModal />
      </LdsModalProvider>
    </div>
  );
}

export default App;
